import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { adminV2AuthGuard } from './modules/admin-v2/guards/admin-v2-auth.guard';
import { authGuard } from './services/guards/auth.guard';
import { publicViewV3Guard } from './modules/league-v3/guards/public-view-v3.guard';

const routes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
            import('./modules/public/landing-page/landing-page.component').then(
                m => m.LandingPageComponent
            ),
    },
    {
        path: 'core',
        loadChildren: () => import('./modules/core/standalone/standalone.module').then(m => m.StandaloneModule),
    },
    {
        path: 'admin',
        data: { breadcrumb: 'admin' },
        canActivate: [authGuard],
        loadChildren: () => import('./modules/admin/admin.module').then(m => m.AdminModule),
    },
    {
        path: 'admin-v2',
        data: { breadcrumb: 'admin-v2' },
        canActivate: [adminV2AuthGuard],
        loadChildren: () => import('./modules/admin-v2/admin-v2.routes').then(m => m.ADMIN_V2_ROUTES),
    },
    {
        // Glassmorphism design-system rebuild of admin-v2 — see
        // docs/1.17.8.26-glassmorphism-design-system-admin-v3-foundation.md.
        // This is the DEFAULT admin as of 1.32.8.26 — login redirects here.
        // admin-v2 is retained below (hidden, reachable only by direct URL) as a
        // safety net until it is deleted in a later version. Reuses the same
        // auth guard — no separate admin-v3 session/auth concept.
        path: 'admin-v3',
        data: { breadcrumb: 'admin-v3' },
        canActivate: [adminV2AuthGuard],
        loadChildren: () => import('./modules/admin-v3/admin-v3.routes').then(m => m.ADMIN_V3_ROUTES),
    },
    {
        path: 'login',
        pathMatch: 'full',
        loadComponent: () =>
            import('./modules/admin-v2/auth/auth-page/auth-page.component').then(m => m.AuthPageComponent),
    },
    {
        path: 'forgot-password',
        pathMatch: 'full',
        loadComponent: () =>
            import('./modules/admin-v2/auth/forgot-password-page/forgot-password-page.component').then(
                m => m.ForgotPasswordPageComponent
            ),
    },
    {
        path: 'reset-password',
        pathMatch: 'full',
        loadComponent: () =>
            import('./modules/admin-v2/auth/reset-password-page/reset-password-page.component').then(
                m => m.ResetPasswordPageComponent
            ),
    },
    // Self-registration flows (team, manager, player, roster photos) — registration-v3 is a copy of
    // registration-v2 that the glass look-and-feel is being migrated into. registration-v2 is retained
    // (unrouted) as a reference until it is deleted in a later version.
    {
        path: 'register',
        loadChildren: () =>
            import('./modules/registration-v3/registration-v3.routes').then(m => m.REGISTER_V3_ROUTES),
    },
    {
        path: 'manage',
        loadChildren: () =>
            import('./modules/registration-v3/registration-v3.routes').then(m => m.MANAGE_V3_ROUTES),
    },
    {
        path: 'player-register',
        loadChildren: () =>
            import('./modules/registration-v3/registration-v3.routes').then(m => m.PLAYER_REGISTER_V3_ROUTES),
    },
    {
        path: 'roster-photos',
        loadChildren: () =>
            import('./modules/registration-v3/registration-v3.routes').then(m => m.ROSTER_PHOTO_V3_ROUTES),
    },
    {
        path: 'verify-email',
        loadComponent: () =>
            import('./modules/admin-v2/auth/verify-email/verify-email.component').then(
                m => m.VerifyEmailComponent
            ),
    },
    {
        // Public league view — glassmorphism (league-v3). This is the DEFAULT
        // public view as of 1.32.8.26: matched for every league except those
        // that explicitly opted out (publicViewStyle === 'classic'), and except
        // unresolvable codes — both fall through to the league-v2 tree below.
        path: ':id',
        canMatch: [publicViewV3Guard],
        loadChildren: () => import('./modules/league-v3/league-v3.routes').then(m => m.LEAGUE_V3_ROUTES),
    },
    {
        path: ':id',
        loadChildren: () => import('./modules/league-v2/league-v2.routes').then(m => m.LEAGUE_V2_ROUTES),
    },
];

@NgModule({
    imports: [RouterModule.forRoot(routes, { enableTracing: true })],
    exports: [RouterModule],
})
export class AppRoutingModule {}
